import { useState, useEffect } from 'react';
import { db } from '../firebase/config';
import { collection, getDocs, addDoc, deleteDoc } from 'firebase/firestore';
import { dedupeSoftware, softwareKey } from '../utils/softwareNames';

const savedAt = d => {
  const t = d.data().createdAt;
  if (!t) return 0; // äldre dokument saknar tidsstämpel – räknas som först sparade
  return typeof t.toMillis === 'function' ? t.toMillis() : new Date(t).getTime() || 0;
};

/**
 * Det gemensamma mjukvaruregistret (softwareTags).
 *
 * `pool` är registret i bokstavsordning, unikt utan hänsyn till versaler.
 * `registry` är registret där varianter som "BIM 360" och "BIM360" är
 * sammanslagna och den först sparade stavningen vunnit, i bokstavsordning.
 */
export function useSoftwareTags() {
  const [pool, setPool] = useState([]);
  const [registry, setRegistry] = useState([]);

  useEffect(() => {
    async function loadAndClean() {
      const snap = await getDocs(collection(db, 'softwareTags'));
      const seen = new Map(); // lowercase → { name, ref }
      const toDelete = [];

      snap.docs.forEach(d => {
        const name = (d.data().name || '').trim();
        if (!name) { toDelete.push(d.ref); return; }
        const key = name.toLowerCase();
        if (seen.has(key)) {
          toDelete.push(d.ref); // duplicate — schedule for deletion
        } else {
          seen.set(key, { name, ref: d.ref });
        }
      });

      // Clean duplicates in background — fire-and-forget.
      // Medvetet bara exakta dubbletter utan hänsyn till versaler: varianter
      // med olika mellanslag slås ihop vid visning, aldrig genom att radera.
      if (toDelete.length > 0) {
        Promise.all(toDelete.map(ref => deleteDoc(ref))).catch(() => {});
      }

      const deduped = [...seen.values()]
        .map(v => v.name)
        .sort((a, b) => a.localeCompare(b, 'sv'));
      setPool(deduped);

      const oldestFirst = [...snap.docs]
        .sort((a, b) => savedAt(a) - savedAt(b))
        .map(d => d.data().name);
      setRegistry(dedupeSoftware(oldestFirst).sort((a, b) => a.localeCompare(b, 'sv')));
    }

    loadAndClean().catch(() => {});
  }, []);

  async function addToPool(name) {
    const trimmed = name.trim();
    // En variant av ett namn som redan finns ("BIM360" när "BIM 360" finns)
    // läggs inte till som ett nytt namn.
    if (!trimmed || pool.some(p => softwareKey(p) === softwareKey(trimmed))) return;
    try {
      await addDoc(collection(db, 'softwareTags'), { name: trimmed, createdAt: new Date() });
      setPool(prev => [...prev, trimmed].sort((a, b) => a.localeCompare(b, 'sv')));
      setRegistry(prev => [...prev, trimmed].sort((a, b) => a.localeCompare(b, 'sv')));
    } catch {}
  }

  return { pool, registry, addToPool };
}
